import { Breadcrumb, BreadcrumbItem, Stack } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AlertDecisionTag } from "src/components/Alerts/AlertDecisionTag";
import { BackButtonWithCondition } from "src/components/Buttons";
import { CaseArchivedTag } from "src/components/case/CaseArchivedTag";
import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError.tsx";
import { ModalTransition } from "src/components/Modal";
import { InlineNotification } from "src/components/Notifications";
import PageHeader from "src/components/PageHeader.tsx";
import { SelectedTabType } from "src/components/TabbedView/useTabNavigation.ts";
import { DynamicViewRenderer } from "src/dynamic-view-renderer/DynamicViewRenderer.tsx";
import { useViewDefinition } from "src/dynamic-view-renderer/Views/useViewDefinition";
import {
  AlertCaseVersionsDiffModal,
  hasCaseVersionsChanged,
  OutdatedAlertCaseWarning,
} from "src/features/case/CaseVersionsDiff";
import { useGetAlertById } from "src/graphql/anti-fraud/queries/alerts/GetAlertByIdQuery.ts";
import { useGetAlertCase } from "src/hooks/useGetAlertCase";
import { useT } from "src/internationalization";
import ValidateAlertModalButtons from "src/pages/AlertDetails/ValidateAlertModalButtons.tsx";

const AlertDetailsPage: React.FC = () => {
  const t = useT();

  const { id } = useParams<{ id: string; code: string }>();
  const getAlertCase = useGetAlertCase();

  const { data: alert, error, loading, refetch } = useGetAlertById({ id });
  const diffModal = useModal();

  const expectedCaseVersion = alert ? getAlertCase(alert) : null;
  const caseVersion = expectedCaseVersion ?? alert?.currentCase ?? null;

  const viewDefinition = useViewDefinition("AlertDetailsView", caseVersion?.typeCode);
  const viewConfiguration = viewDefinition?.view;

  const hasCaseChanged = useMemo(() => {
    if (!alert || !caseVersion) return null;
    if (!alert.creationCase) return false;
    if (caseVersion.id === alert.currentCase.id) return false;

    return hasCaseVersionsChanged(alert.creationCase, {
      validation: alert.validationCase,
      current: alert.currentCase,
    });
  }, [alert, caseVersion]);

  const [_selectedTab, setSelectedTab] = useState<SelectedTabType | undefined>();

  if (error) return <InlineError {...error} />;
  if (loading) return <Loading />;

  return (
    <>
      <Stack gap={5}>
        <Breadcrumb noTrailingSlash={true}>
          <BreadcrumbItem>
            <Link to="/alerts/notAssigned">{t("page.alert.breadcrumb.alerts")}</Link>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage={true}>{alert?.alertNumber}</BreadcrumbItem>
        </Breadcrumb>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <div className="flex items-center">
              <BackButtonWithCondition />
              <PageHeader>{t("page.alert.title", { number: alert?.alertNumber })}</PageHeader>
            </div>
            {alert && alert.currentCase && (
              <ValidateAlertModalButtons alert={alert} caseId={alert.currentCase?.id} />
            )}
          </div>
          {expectedCaseVersion && expectedCaseVersion.snapshotCreation && (
            <CaseArchivedTag date={expectedCaseVersion.snapshotCreation} />
          )}
          {alert && alert.decision && alert.origin === "manual" && (
            <AlertDecisionTag
              decision={alert.decision}
              financialLossAmount={alert.financialLossAmount}
              preventedFraudAmount={alert.preventedFraudAmount}
            />
          )}
        </div>

        {viewConfiguration && expectedCaseVersion === null && (
          <InlineNotification
            kind="warning"
            title={t("general.attention")}
            subtitle={t("caseVersions.noVersionFound.error.message")}
          />
        )}

        {viewConfiguration && hasCaseChanged && (
          <OutdatedAlertCaseWarning onClick={diffModal.openModal} />
        )}

        {viewConfiguration && caseVersion && alert && (
          <DynamicViewRenderer
            viewConfiguration={viewConfiguration}
            data={{
              widgetsData: {
                origin: alert.origin,
                basic: {
                  case: {
                    propertyValues: caseVersion.properties,
                    elements: caseVersion.elements,
                    blacklistedIdentifiers: alert.currentCase.blacklistedIdentifiers,
                    code: caseVersion.typeCode,
                  },
                  propertyValueRendererParams: {
                    CaseLink: {
                      id: caseVersion?.id.toString() ?? "",
                      caseTypeCode: caseVersion.typeCode,
                    },
                    IdentifierGraph: {
                      snapshotCreation: caseVersion.snapshotCreation,
                    },
                  },
                },
                rules: {
                  scenarioExecutionResult: alert?.scenarioExecutionResult,
                },
                alertWorkflow: {
                  alert,
                  refetch,
                },
                chronology: {
                  alert,
                },
                relatedCases: {
                  elements: caseVersion.elements,
                  propertyValues: caseVersion.properties,
                  caseId: caseVersion.id,
                  alertId: alert.id,
                  snapshotCreation: caseVersion.snapshotCreation,
                },
                googleStreetView: {
                  case: caseVersion,
                },
                googleMaps: {
                  case: caseVersion,
                },
              },
            }}
            pathPrefix="/alert/:id"
            onNavigate={setSelectedTab}
          />
        )}
        {!viewConfiguration && <InlineError message={t("general.view.caseType.missing.error")} />}
      </Stack>

      <ModalTransition>
        {diffModal.isModalOpen && alert?.creationCase && (
          <AlertCaseVersionsDiffModal
            creationCase={alert.creationCase}
            validationCase={alert.validationCase}
            currentCase={alert.currentCase}
            alertNumber={alert.alertNumber}
            onClose={diffModal.closeModal}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default AlertDetailsPage;
